import { type components } from "@njbsoft/types/customer";
import { assemblyTypeLabel, classifyVerdict, type StatusKind } from "./device-label";
import { compareNames, compareOrdinal, dateOf } from "./ordering";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];
type CustomerFacility = components["schemas"]["CustomerFacility"];

export type DeviceStatusFilter =
  | "needs_test"
  | "failed"
  | "past_due"
  | "never_tested"
  | "under_review"
  | "on_schedule"
  | "out_of_service"
  | "removed";

/** The `status` values the Devices tab filters by, in the order its menu lists them. */
export const DEVICE_STATUS_FILTERS: readonly DeviceStatusFilter[] = [
  "needs_test",
  "failed",
  "past_due",
  "never_tested",
  "under_review",
  "on_schedule",
  "out_of_service",
  "removed",
];

export type DeviceSort = "urgency" | "property" | "type";

/** The Devices tab's sorts, in the order its menu lists them. */
export const DEVICE_SORTS: readonly DeviceSort[] = ["urgency", "property", "type"];

export interface DeviceFilter {
  q?: string;
  property?: string;
  status?: DeviceStatusFilter;
}

const URGENCY: readonly StatusKind[] = [
  "failed",
  "pastDue",
  "neverTested",
  "underReview",
  "onSchedule",
  "outOfService",
  "removed",
];

// The date each kind is ordered by inside its rank; never tested, out of
// service and removed have none.
const DATE_FIELD: Record<StatusKind, "last_test_date" | "next_test_due_date" | null> = {
  failed: "last_test_date",
  pastDue: "next_test_due_date",
  neverTested: null,
  underReview: "last_test_date",
  onSchedule: "next_test_due_date",
  outOfService: null,
  removed: null,
};

// Failed and past-due devices tie-break straight to the serial, as Home's
// needs-attention list orders them; every other kind breaks a tie on the
// property name first.
const SKIPS_PROPERTY: ReadonlySet<StatusKind> = new Set<StatusKind>(["failed", "pastDue"]);

const STATUS_FILTER_KINDS: Record<DeviceStatusFilter, readonly StatusKind[]> = {
  needs_test: ["failed", "pastDue", "neverTested"],
  failed: ["failed"],
  past_due: ["pastDue"],
  never_tested: ["neverTested"],
  under_review: ["underReview"],
  on_schedule: ["onSchedule"],
  out_of_service: ["outOfService"],
  removed: ["removed"],
};

function propertyName(
  item: CustomerInstallation,
  facilitiesById: ReadonlyMap<string, CustomerFacility>,
): string | undefined {
  return facilitiesById.get(item.facility_id)?.name;
}

/**
 * Most urgent first: failed, past due, never tested, under review, on schedule,
 * out of service, removed. Inside a kind, by the date that kind is told by
 * (the last test for failed and under review, the due date for past due and on
 * schedule), a missing one last; then by property name for every kind but
 * failed and past due; then serial, then id. The three needs-a-test kinds order
 * exactly as Home's needs-attention list.
 */
export function compareUrgency(
  a: CustomerInstallation,
  b: CustomerInstallation,
  facilitiesById: ReadonlyMap<string, CustomerFacility>,
): number {
  const kindA = classifyVerdict(a.verdict);
  const kindB = classifyVerdict(b.verdict);
  const rank = URGENCY.indexOf(kindA) - URGENCY.indexOf(kindB);
  if (rank !== 0) return rank;
  const field = DATE_FIELD[kindA];
  return (
    (field ? compareOrdinal(dateOf(a[field]), dateOf(b[field])) : 0) ||
    (SKIPS_PROPERTY.has(kindA)
      ? 0
      : compareNames(propertyName(a, facilitiesById), propertyName(b, facilitiesById))) ||
    compareOrdinal(a.serial_number, b.serial_number) ||
    compareOrdinal(a.id, b.id)
  );
}

/**
 * A sorted copy: `urgency` is `compareUrgency`; `property` is the property
 * name (a missing one last), then urgency; `type` is the assembly type as the
 * nameplate prints it (a missing or unknown one last), then urgency.
 */
export function sortDevices(
  items: readonly CustomerInstallation[],
  sort: DeviceSort,
  facilitiesById: ReadonlyMap<string, CustomerFacility>,
): CustomerInstallation[] {
  const lead = (a: CustomerInstallation, b: CustomerInstallation): number => {
    if (sort === "property") {
      return compareNames(propertyName(a, facilitiesById), propertyName(b, facilitiesById));
    }
    if (sort === "type") {
      return compareOrdinal(assemblyTypeLabel(a.assembly_type), assemblyTypeLabel(b.assembly_type));
    }
    return 0;
  };
  return [...items].sort((a, b) => lead(a, b) || compareUrgency(a, b, facilitiesById));
}

function matchesSearch(item: CustomerInstallation, needle: string): boolean {
  return [
    item.serial_number,
    item.manufacturer_label,
    item.model_label,
    item.installation_location_description,
  ].some((value) => !!value && value.toLowerCase().includes(needle));
}

/**
 * The devices every given filter keeps, in their input order. `q` (trimmed,
 * case-insensitive) matches inside the serial, make, model and location only;
 * `property` is a facility id; `status` keeps its kinds, `needs_test` being
 * failed, past due and never tested.
 */
export function filterDevices(
  items: readonly CustomerInstallation[],
  filter: DeviceFilter,
  _facilitiesById: ReadonlyMap<string, CustomerFacility>,
): CustomerInstallation[] {
  const needle = filter.q?.trim().toLowerCase() ?? "";
  const kinds = filter.status ? STATUS_FILTER_KINDS[filter.status] : null;
  return items.filter(
    (item) =>
      (needle === "" || matchesSearch(item, needle)) &&
      (!filter.property || item.facility_id === filter.property) &&
      (!kinds || kinds.includes(classifyVerdict(item.verdict))),
  );
}
